import { useEffect, useState } from "react";
import { FileUp, Loader2, ArrowRight, ArrowLeft, CreditCard, FileText, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader, Panel } from "@/components/Layout";
import { api, formatError } from "@/lib/api";
import { EmailTypoHint } from "@/components/EmailTypoHint";
import { addressIssues } from "@/lib/addressCheck";
import { AddressPicker } from "@/components/AddressPicker";
import { useAuth } from "@/context/AuthContext";

const AddressWarnings = ({ form, testId, onEdit }) => {
  const issues = addressIssues(form);
  if (!issues.length) return null;
  return (
    <div data-testid={testId} className="sm:col-span-2 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 p-3 text-sm text-amber-800 dark:text-amber-300">
      <p className="font-semibold flex items-center gap-2"><AlertTriangle size={15} /> Check the address</p>
      <ul className="mt-1 ml-6 list-disc space-y-0.5 text-xs">{issues.map((i) => <li key={i}>{i}</li>)}</ul>
      <p className="mt-2 text-xs">The recipient must type this address to claim the letter, so mistakes here can lock them out.{onEdit && <> <button type="button" data-testid={`${testId}-edit`} onClick={onEdit} className="underline font-semibold">Edit address</button></>}</p>
    </div>
  );
};

const RECIPIENT = [
  { id: "recipient_name", label: "Recipient full name", span: 2 },
  { id: "recipient_email", label: "Recipient email", type: "email" },
  { id: "recipient_phone", label: "Recipient mobile (optional, +44…)", type: "tel", optional: true, hint: "Recommended: lets them get their claim code by text if email is slow." },
  { id: "line1", label: "Address line 1", span: 2 },
  { id: "line2", label: "Address line 2 (optional)", optional: true, span: 2 },
  { id: "line3", label: "Address line 3 (optional)", optional: true, span: 2 },
  { id: "city", label: "Town / City" },
  { id: "postcode", label: "Postcode" },
];
const STEPS = ["Letter", "Recipient", "Review & pay"];

const PHONE_TONE = { valid_mobile: "text-emerald-700 dark:text-emerald-400", unverified: "text-amber-700 dark:text-amber-400", not_mobile: "text-red-600", invalid: "text-red-600" };

const Field = ({ f, form, set, check }) => (
  <div className={`space-y-2 ${f.span === 2 ? "sm:col-span-2" : ""}`}>
    <Label htmlFor={f.id}>{f.label}</Label>
    <Input id={f.id} data-testid={`send-${f.id}-input`} type={f.type || "text"} required={!f.optional} value={form[f.id]} onChange={(e) => set({ ...form, [f.id]: e.target.value })} className="h-11" />
    {f.hint && !check && <p data-testid={`send-${f.id}-hint`} className="text-xs text-slate-500">{f.hint}</p>}
    {check && <p data-testid="send-phone-check-result" className={`text-xs font-medium ${PHONE_TONE[check.status]}`}>{check.message}</p>}
    {f.id === "recipient_email" && <EmailTypoHint email={form.recipient_email} onFix={(v) => set({ ...form, recipient_email: v })} testId="send-email-typo" />}
  </div>
);

const Row = ({ k, v }) => <div className="flex justify-between gap-4 py-2 border-b border-slate-100 dark:border-slate-800 text-sm"><span className="text-slate-500">{k}</span><span className="text-right font-medium break-all">{v || "—"}</span></div>;

export default function SendLetter() {
  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ subject: "", recipient_name: "", recipient_email: "", recipient_phone: "", line1: "", line2: "", line3: "", city: "", postcode: "", category: "general", require_id_check: false });
  const [policy, setPolicy] = useState(null);
  useEffect(() => { api.get("/id-policy").then((r) => setPolicy(r.data)).catch(() => setPolicy(null)); }, []);
  const autoId = !!policy && (policy.org_all_letters || policy.required_categories.includes(form.category));
  const needsId = autoId || form.require_id_check;
  const total = `£${(0.6 + (needsId ? 1.5 : 0)).toFixed(2)}`;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [phoneCheck, setPhoneCheck] = useState(null);
  const { user } = useAuth();
  const [saveBook, setSaveBook] = useState(true);
  const [bookScope, setBookScope] = useState("private");
  const canOrgBook = (user?.org?.perms || []).includes("letters:send");

  const next = (e) => { e.preventDefault(); setError(""); setStep(step + 1); };

  const review = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.recipient_phone.trim()) { setPhoneCheck(null); setStep(2); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/phone/check", { phone: form.recipient_phone });
      setPhoneCheck(data);
      if (data.ok) {
        setForm({ ...form, recipient_phone: data.normalized });
        setStep(2);
      }
    } catch (err) {
      setError(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  const pay = async () => {
    setBusy(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      const { data: mail } = await api.post("/mail", fd);
      if (saveBook) {
        await api.post("/address-book", { scope: bookScope, name: form.recipient_name, email: form.recipient_email, phone: form.recipient_phone, line1: form.line1, line2: form.line2, line3: form.line3, city: form.city, postcode: form.postcode }).catch(() => null);
      }
      const { data } = await api.post(`/mail/${mail.id}/checkout`, { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(formatError(err));
      setBusy(false);
    }
  };

  return (
    <div data-testid="send-letter-page" className="max-w-4xl">
      <PageHeader eyebrow="New verified letter" title="Send a letter" />
      <ol className="flex gap-2 mb-8" data-testid="send-steps">
        {STEPS.map((s, i) => (
          <li key={s} className={`flex-1 text-xs font-semibold uppercase tracking-wider pt-3 border-t-2 ${i <= step ? "border-emerald-600 text-slate-900 dark:text-white" : "border-slate-200 dark:border-slate-800 text-slate-400"}`}>{`0${i + 1} ${s}`}</li>
        ))}
      </ol>
      {step === 0 && (
        <Panel>
          <form onSubmit={next} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="subject">Subject (visible to you and the recipient after claim)</Label>
              <Input id="subject" data-testid="send-subject-input" required minLength={2} value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="h-11" />
            </div>
            <label htmlFor="pdf" className="block cursor-pointer rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-700 hover:border-emerald-500 transition-colors p-10 text-center">
              {file ? <FileText className="mx-auto text-emerald-600" /> : <FileUp className="mx-auto text-slate-400" />}
              <p className="mt-3 text-sm font-medium" data-testid="send-file-name">{file ? file.name : "Choose a PDF letter (max 10 MB)"}</p>
              <input id="pdf" data-testid="send-file-input" type="file" accept="application/pdf" className="sr-only" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
            <div className="flex justify-end"><Button data-testid="send-step1-next" type="submit" disabled={!file} className="rounded-full h-11 px-6">Continue <ArrowRight size={16} className="ml-1" /></Button></div>
          </form>
        </Panel>
      )}
      {step === 1 && (
        <Panel>
          <form onSubmit={review} className="space-y-6">
            <div className="grid sm:grid-cols-2 gap-4"><AddressPicker onPick={(v) => { setPhoneCheck(null); setForm({ ...form, ...v }); }} />{RECIPIENT.map((f) => <Field key={f.id} f={f} form={form} set={setForm} check={f.id === "recipient_phone" ? phoneCheck : null} />)}<AddressWarnings form={form} testId="send-address-warnings" /></div>
            {policy && (
              <div className="rounded-xl border border-slate-200 dark:border-slate-800 p-4 grid sm:grid-cols-2 gap-4 items-start" data-testid="send-id-options">
                <div className="space-y-2">
                  <Label htmlFor="category">Letter category</Label>
                  <select id="category" data-testid="send-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="h-11 w-full rounded-md border border-input bg-background px-3 text-sm capitalize">
                    {policy.categories.map((c) => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
                  </select>
                </div>
                <label className="flex items-start gap-3 text-sm pt-7">
                  <input type="checkbox" data-testid="send-require-id-checkbox" checked={needsId} disabled={autoId} onChange={(e) => setForm({ ...form, require_id_check: e.target.checked })} className="h-4 w-4 mt-0.5 accent-emerald-600" />
                  <span><span className="font-semibold">Require ID check (+£1.50)</span>
                    <span className="block text-xs text-slate-500" data-testid="send-id-hint">{autoId ? (policy.org_all_letters ? "Required by your organisation for every letter." : "Required for this category.") : "The recipient verifies a photo ID and selfie before opening. Only the result is kept."}</span></span>
                </label>
              </div>
            )}
            <p className="text-xs text-slate-500">The recipient must confirm this exact name and postal address (case, punctuation and common abbreviations are ignored) before the letter is released.</p>
            {error && <p data-testid="send-step2-error" className="text-sm text-red-600">{error}</p>}
            <div className="flex justify-between">
              <Button type="button" variant="outline" data-testid="send-step2-back" onClick={() => setStep(0)} className="rounded-full h-11"><ArrowLeft size={16} className="mr-1" /> Back</Button>
              <Button data-testid="send-step2-next" type="submit" disabled={busy} className="rounded-full h-11 px-6">{busy && <Loader2 size={16} className="animate-spin mr-1" />}Review <ArrowRight size={16} className="ml-1" /></Button>
            </div>
          </form>
        </Panel>
      )}
      {step === 2 && (
        <div className="grid lg:grid-cols-5 gap-6">
          <Panel title="Review" className="lg:col-span-3">
            <Row k="Letter" v={file?.name} />
            <Row k="Subject" v={form.subject} />
            <Row k="Recipient" v={form.recipient_name} />
            <Row k="Email" v={form.recipient_email} />
            <div className="sm:col-span-2"><EmailTypoHint email={form.recipient_email} onFix={(v) => setForm({ ...form, recipient_email: v })} testId="send-review-email-typo" /></div>
            <Row k="Mobile" v={form.recipient_phone ? `${form.recipient_phone}${phoneCheck?.status === "valid_mobile" ? " (checked)" : ""}` : ""} />
            <Row k="Address" v={[form.line1, form.line2, form.line3, form.city, form.postcode.toUpperCase()].filter(Boolean).join(", ")} />
            <div className="mt-3"><AddressWarnings form={form} testId="send-review-address-warnings" onEdit={() => setStep(1)} /></div>
            <Row k="ID check" v={needsId ? "Required (+£1.50)" : "Not required"} />
            <div className="mt-4 flex flex-wrap items-center gap-3 text-sm">
              <label className="inline-flex items-center gap-2 cursor-pointer"><input type="checkbox" data-testid="send-save-to-book" checked={saveBook} onChange={(e) => setSaveBook(e.target.checked)} className="h-4 w-4 accent-emerald-600" /> Save this recipient to address book</label>
              {saveBook && canOrgBook && <select data-testid="send-save-book-scope" value={bookScope} onChange={(e) => setBookScope(e.target.value)} className="h-9 rounded-md border border-input bg-background px-2 text-xs"><option value="private">Private</option><option value="org">Organisation (shared)</option></select>}
            </div>
          </Panel>
          <div className="lg:col-span-2 rounded-xl bg-slate-900 text-white p-6 flex flex-col">
            <p className="text-[11px] uppercase tracking-wider text-emerald-400 font-semibold">Total</p>
            <p className="font-display text-5xl font-bold mt-2" data-testid="send-total">{total}</p>
            <p className="text-sm text-slate-400 mt-2">One verified send · 14-day claim window · reminders on day 7 & 12.</p>
            {error && <p data-testid="send-error" className="text-sm text-red-400 mt-4">{error}</p>}
            <button data-testid="send-pay-btn" onClick={pay} disabled={busy} className="mt-auto pt-0 inline-flex items-center justify-center gap-2 h-12 rounded-full bg-emerald-600 font-semibold hover:bg-emerald-500 transition-colors disabled:opacity-60 mt-6">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />} Pay {total} & send
            </button>
            <button data-testid="send-step3-back" onClick={() => setStep(1)} className="mt-3 text-sm text-slate-400 hover:text-white">Edit details</button>
          </div>
        </div>
      )}
    </div>
  );
}
